import { Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptTypeSpeak, IDialogPromptSentence } from "../../api/DialogPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { useSentenceChinesePinyin } from "../../hook/useSentenceChinesePinyin";
import { useStudentResponsePinyin } from "../../hook/useStudentResponsePinyin";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptTypeSpeakFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string | null;
    imageSource: string | null;
    imageSourceUrl: string | null;
}

export const DialogPromptTypeSpeakForm: React.FC<DialogPromptTypeSpeakFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const {
        text: teacherChinesePrompt,
        pinyin: teacherChinesePromptPinyin,
        pinyinList: teacherChinesePromptPinyinList,
        pinyinErrorText: teacherChinesePromptPinyinErrorText,
        setDialogPromptFields: setTeacherChinesePromptFields,
        clearDialogPromptFields: clearTeacherChinesePromptFields,
        clearErrorText: clearTeacherChinesePromptPinyinErrorText,
        handleTextChange: handleTeacherChinesePromptChange,
        handlePinyinChange: handleTeacherChinesePromptPinyinChange,
    } = useSentenceChinesePinyin({ history, location, setErrorMessage });

    const {
        studentResponsePrompt,
        studentResponsePromptErrorText,
        studentResponseList,
        studentResponseListErrorText,
        studentResponsePinyinList,
        studentResponsePinyinErrorText,
        setStudentResponseFields,
        clearStudentResponseFields,
        clearStudentResponseErrors,
        handleStudentResponsePromptChange,
        handleStudentResponseListChange,
        handleStudentResponsePinyinListChange,
    } = useStudentResponsePinyin({ history, location, setErrorMessage });

    const [teacherAskStudentToSpeakPrompt, setTeacherAskStudentToSpeakPrompt] = React.useState("");
    const [spokenYesResponse, setSpokenYesResponse] = React.useState("");
    const [spokenNoResponse, setSpokenNoResponse] = React.useState("");


    const handleTeacherAskStudentToSpeakPromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherAskStudentToSpeakPrompt(e.target.value); };
    const handleSpokenYesResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSpokenYesResponse(e.target.value); };
    const handleSpokenNoResponseChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSpokenNoResponse(e.target.value); };

    const setDialogPromptFields = React.useCallback((dialogPrompt: DialogPromptTypeSpeak) => {
        if (dialogPrompt.class_name == "DialogPromptTypeSpeakModel") {

            setTeacherChinesePromptFields(dialogPrompt.teacher_chinese_prompt, dialogPrompt.teacher_chinese_prompt_pinyin, dialogPrompt.teacher_chinese_prompt_sentence_id)

            setStudentResponseFields(dialogPrompt.student_response_prompt, dialogPrompt.dialog_prompt_sentence_list)

            setTeacherAskStudentToSpeakPrompt(dialogPrompt.teacher_ask_student_to_speak_prompt ? dialogPrompt.teacher_ask_student_to_speak_prompt : "")
            setSpokenYesResponse(dialogPrompt.spoken_yes_response ? dialogPrompt.spoken_yes_response : "")
            setSpokenNoResponse(dialogPrompt.spoken_no_response ? dialogPrompt.spoken_no_response : "")
        } else {
            clearTeacherChinesePromptFields()
            clearStudentResponseFields()
            setTeacherAskStudentToSpeakPrompt("");
            setSpokenYesResponse("");
            setSpokenNoResponse("");
        }
    }, [setTeacherChinesePromptFields, clearTeacherChinesePromptFields, setStudentResponseFields, clearStudentResponseFields, setTeacherAskStudentToSpeakPrompt, setSpokenYesResponse, setSpokenNoResponse])

    React.useEffect(() => {
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptTypeSpeak;
                setDialogPromptFields(oldDialogPrompt);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearTeacherChinesePromptPinyinErrorText()
        clearStudentResponseErrors()
    }, [oldDialogPromptId, clearStudentResponseErrors, clearTeacherChinesePromptPinyinErrorText, history, location, setErrorMessage, setDialogPromptFields])


    React.useEffect(() => {
        // Convert newline-separated string to array
        const studentResponseListList = studentResponseList.split("\n")
        const studentResponsePinyinListList = studentResponsePinyinList.split("\n")
        const dialogPromptSentenceList: IDialogPromptSentence[] = studentResponseListList.map((response: string, index: number) => {
            const pinyinRaw = studentResponsePinyinListList.length > index ? studentResponsePinyinListList[index] : '';
            const pinyinList = pinyinRaw.trim().split(/\s+/).filter(Boolean);

            return {
                id: -1,
                dialog_prompt_id: -1,
                sentence_id: -1,
                student_response: response,
                student_response_pinyin: pinyinList,
                order_index: -1
            };
        });

        const dialogPrompt = new DialogPromptTypeSpeak(
            id,
            dialogId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            isHidden,
            yesResponse as string | "ERROR",
            imageSource,
            imageSourceUrl,
            teacherChinesePrompt,
            null, // sentence_id is null
            teacherChinesePromptPinyinList,
            studentResponsePrompt,
            dialogPromptSentenceList,
            teacherAskStudentToSpeakPrompt,
            spokenYesResponse,
            spokenNoResponse
        );
        setNewDialogPrompt(dialogPrompt);
    }, [id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, imageSource, imageSourceUrl,
        teacherChinesePrompt, teacherChinesePromptPinyinList, studentResponsePrompt, studentResponseList, studentResponsePinyinList,
        teacherAskStudentToSpeakPrompt, spokenYesResponse, spokenNoResponse, setNewDialogPrompt])

    return (
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>

            <TextField
                label="Teacher Chinese Prompt"
                value={teacherChinesePrompt}
                onChange={handleTeacherChinesePromptChange}
            />
            <TextField
                label="Teacher Chinese Prompt Pinyin"
                value={teacherChinesePromptPinyin}
                onChange={handleTeacherChinesePromptPinyinChange}
                error={teacherChinesePromptPinyinErrorText != null}
                helperText={teacherChinesePromptPinyinErrorText}
            />
            <div>
                <TextField
                    label="Student Chinese Prompt"
                    value={studentResponsePrompt ? studentResponsePrompt : "<Answer/>"}
                    onChange={handleStudentResponsePromptChange}
                    required
                    sx={{ width: '100%' }}
                    error={studentResponsePromptErrorText != null}
                    helperText={studentResponsePromptErrorText}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted.<br />
                    The prompt will be skipped and not displayed, if this field is only &lt;Answer/&gt;
                </Typography>
            </div>
            <TextField
                label="Allowed Student Prompt Responses"
                value={studentResponseList}
                onChange={handleStudentResponseListChange} // just store value on change
                onBlur={handleStudentResponseListChange} // full processing logic goes here
                multiline
                rows={6}
                sx={{ width: '100%' }}
                required
                error={studentResponseListErrorText != null}
                helperText={studentResponseListErrorText}
            />
            <TextField
                label="Allowed Student Prompt Response Pinyin"
                value={studentResponsePinyinList}
                onChange={handleStudentResponsePinyinListChange}
                multiline
                rows={6}
                sx={{ width: '100%' }}
                required
                error={studentResponsePinyinErrorText != null}
                helperText={studentResponsePinyinErrorText}
            />
            <TextField
                label="Teacher Ask Student To Speak Prompt"
                value={teacherAskStudentToSpeakPrompt}
                onChange={handleTeacherAskStudentToSpeakPromptChange}
                required
            />
            <TextField
                label="Student Spoken Well Prompt"
                value={spokenYesResponse}
                onChange={handleSpokenYesResponseChange}
                required
            />
            <TextField
                label="Student Spoken Poorly Prompt"
                value={spokenNoResponse}
                onChange={handleSpokenNoResponseChange}
                required
            />
        </Stack>
    )
}
